<template>
  <OnboardingLayout>
    <OnboardingFeaturePanel />
    <div class="signup">
      <div class="signup__intro">
        <h1 class="text-h4 font-weight-medium">
          {{ $t('signup.v2.title') }}
        </h1>
        <p class="text-body-1">
          {{ $t('signup.v2.subtitle') }}
        </p>
      </div>

      <v-form
        ref="signupForm"
        name="signup"
        class="signup__form"
        @submit.prevent="signup"
      >
        <div class="signup__name-row">
          <OnboardingTextField
            v-model.trim="firstname"
            :label="$t('signup.labels.firstname')"
            prepend-inner-icon="mdi-account-outline"
            autocomplete="given-name"
            :rules="[rules.required]"
            required
          />
          <OnboardingTextField
            v-model.trim="lastname"
            :label="$t('signup.labels.lastname')"
            prepend-inner-icon="mdi-account-outline"
            autocomplete="family-name"
            :rules="[rules.required]"
            required
          />
        </div>

        <OnboardingTextField
          v-model.trim="email"
          :label="$t('signup.labels.email')"
          type="email"
          prepend-inner-icon="mdi-email-outline"
          autocomplete="email"
          :rules="[rules.email, rules.required]"
          :error-messages="emailError ? [emailError] : []"
          required
        />

        <OnboardingTextField
          v-model="password"
          :label="$t('signup.labels.password')"
          :type="showPassword ? 'text' : 'password'"
          prepend-inner-icon="mdi-lock-outline"
          :append-inner-icon="showPassword ? 'mdi-eye' : 'mdi-eye-off'"
          autocomplete="new-password"
          :rules="[rules.required]"
          required
          @click:append-inner="showPassword = !showPassword"
        />

        <i18n-t
          keypath="signup.v2.privacy-hint"
          tag="p"
          class="text-body-2 text-grey signup__hint"
          scope="global"
        >
          <template #privacy>
            <a
              :href="publicDataPrivacyUrl"
              target="_blank"
              rel="noopener noreferrer"
              >{{ $t('signup.v2.links.privacy') }}</a
            >
          </template>
        </i18n-t>

        <v-expand-transition>
          <v-alert
            v-if="alertError"
            type="error"
            density="compact"
            variant="tonal"
            rounded="0"
          >
            <span v-html="$t(alertMessage)" />
          </v-alert>
        </v-expand-transition>

        <v-btn
          type="submit"
          color="primary"
          height="50"
          rounded="xl"
          block
          class="signup__submit"
          :loading="submitting"
        >
          {{ $t('signup.v2.submit') }}
        </v-btn>
      </v-form>

      <p class="text-body-2 text-grey">
        {{ $t('signup.account-exists') }}
        <a
          href="/auth/login"
          class="ml-2 font-weight-bold text-primary"
          >{{ $t('login.form.submit') }}</a
        >
      </p>
    </div>
  </OnboardingLayout>
</template>

<script setup lang="ts">
import { useApolloClient } from '@vue/apollo-composable'
import { computed, getCurrentInstance, inject, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute, useRouter } from 'vue-router'
import type { VForm } from 'vuetify/components'

import { useBranding } from '@/composables/useBranding.ts'
import { isValidPassword } from '@/lib/password'
import { extractUtmParams } from '@/lib/utmParams.js'
import { email as emailRule, required } from '@/lib/validation'
import brandingMixin from '@/mixins/branding.js'
import featureMixin from '@/mixins/feature.js'
import { TrackingPlugin } from '@/plugins/tracking'
import SIGN_UP_US_USER from '@/queries/SignUpUSUser.gql'

import OnboardingFeaturePanel from './components/OnboardingFeaturePanel.vue'
import OnboardingLayout from './components/OnboardingLayout.vue'
import OnboardingTextField from './components/OnboardingTextField.vue'
import { useBetaOnboarding } from './composables/useBetaOnboarding.ts'

defineOptions({
  name: 'SignupPage',
  apollo: { $client: 'public' },
  mixins: [featureMixin, brandingMixin],
})

const proxy = getCurrentInstance()!.proxy as any
const route = useRoute()
const router = useRouter()
const onboarding = useBetaOnboarding()
const { client } = useApolloClient()

const signupForm = ref<VForm | null>(null)
const firstname = ref('')
const lastname = ref('')
const email = ref('')
const password = ref('')
const showPassword = ref(false)
const submitting = ref(false)

const emailError = ref('')
const alertError = ref<string | null>(null)

const { t } = useI18n()
const rules = {
  required,
  email: emailRule,
}

const { publicDataPrivacyUrl } = useBranding()
const $tracking = inject<TrackingPlugin>('tracking')

const alertMessages: Record<string, string> = {
  emptyFields: 'alerts.signup.empty-fields',
  invalidToken: 'alerts.signup.invalid-token',
  invalidAgreement: 'alerts.signup.invalid-agreement',
  invalidPassword: 'alerts.signup.invalid-password',
  unknown: 'alerts.signup.unknown',
}
const alertMessage = computed(() =>
  alertError.value ? (alertMessages[alertError.value] ?? alertMessages.unknown) : ''
)

watch(email, () => (emailError.value = ''))

async function signup() {
  $tracking.event('SignUp Account', 'Submitted')
  alertError.value = null
  emailError.value = ''

  const { valid } = await signupForm.value!.validate()
  if (!valid) {
    $tracking.event('SignUp Account', 'Validated', 'Invalid', 'Fields incomplete')
    return
  }

  if (!isValidPassword(password.value)) {
    $tracking.event('SignUp Account', 'Validated', 'Invalid', 'Password')
    alertError.value = 'invalidPassword'
    return
  }

  submitting.value = true
  const utmParams = extractUtmParams(route.query)

  const input = {
    firstname: firstname.value,
    lastname: lastname.value,
    email: email.value,
    password: password.value,
    ...(Object.keys(utmParams).length ? { utmParams } : {}),
  }

  let result: { status: string; errorCode?: string | null } | undefined
  try {
    const { data } = await client.mutate({
      mutation: SIGN_UP_US_USER,
      context: { withoutAuth: true },
      variables: { input },
    })
    result = data.signUpUSUser
  } catch (error) {
    $tracking.event('SignUp Account', 'Failed')
    alertError.value = 'unknown'
    submitting.value = false
    return
  }

  if (result?.status === 'ERROR') {
    $tracking.event('SignUp Account', 'Validated', 'Signup failed')
    if (result.errorCode === 'userExists') emailError.value = t('signup.v2.errors.email-taken')
    else alertError.value = result.errorCode || 'unknown'
    submitting.value = false
    return
  }

  $tracking.event('SignUp Account', 'Created')
  onboarding.start({ firstname: firstname.value, email: email.value })

  const consentsPath = router.resolve({ name: 'beta-legal-consents' }).fullPath
  try {
    sessionStorage.setItem('locationBeforeLogin', consentsPath)
  } catch {
    // storage blocked: the user lands on the default page and can still sign in normally
  }

  try {
    await proxy.$auth.login(email.value, password.value)
  } catch (error) {
    sessionStorage.removeItem('locationBeforeLogin')
    submitting.value = false
    await router.replace({ name: 'login' })
  }
}
</script>

<style scoped>
.signup {
  width: 450px;
  max-width: 100%;
  padding: 16px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 24px;
}

.signup__intro {
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 8px;
  color: rgb(var(--v-theme-on-surface));
}

.signup__form {
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.signup__submit {
  letter-spacing: 1px;
  font-size: 14px;
}

.signup__name-row {
  display: grid;
  grid-template-columns: 1fr;
  gap: 16px;
}

@media (max-width: 599px) {
  .signup {
    width: 100%;
    padding: 20px 12px 24px;
    gap: 16px;
  }

  .signup__intro {
    text-align: center;
    gap: 4px;
  }
  .signup__intro h1 {
    font-size: 22px !important;
    line-height: 28px !important;
  }
  .signup__intro p {
    font-size: 13px;
    line-height: 18px;
  }

  .signup__form {
    gap: 12px;
  }

  .signup__name-row {
    grid-template-columns: 1fr 1fr;
    gap: 12px;
  }

  .signup__submit {
    height: 44px !important;
  }
}
</style>
